import CreditCardIcon from "@mui/icons-material/CreditCard";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PaymentGatewayApi, {
  getPaymentReturnUrl,
  normalizePaymentRedirectUrl,
} from "../../../infrastructure/api/paymentGateway";
import CustomerBalanceApi from "../../../infrastructure/api/customerBalance";
import { notifyCustomerFinanceRefresh } from "../customer/HeaderFinanceMenu";
import { toJalaliFullDate } from "../../middleware/middleware";
import PaymentCardSkeleton from "./PaymentCardSkeleton";
import PaymentStatusBadge from "./PaymentStatusBadge";
import {
  formatRial,
  GATEWAY_SESSION,
  getProviderLabel,
  mapSessionToBadgeState,
  mapUiStateToBadgeState,
  normalizePaymentStatus,
  normalizeStartPayment,
  pick,
} from "./paymentGatewayUtils";

const CustomerInvoiceOnlinePayment = ({ invoiceId, compact = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payingFromBalance, setPayingFromBalance] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [panelBalance, setPanelBalance] = useState(null);
  const [allowPayFromBalance, setAllowPayFromBalance] = useState(true);

  const loadStatus = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setError(null);
    try {
      const [statusRes, balanceRes] = await Promise.all([
        PaymentGatewayApi.getInvoicePaymentStatus(invoiceId),
        CustomerBalanceApi.getBalance().catch(() => null),
      ]);
      const { error: err, data } = normalizePaymentStatus(statusRes);
      if (err) {
        setError(err);
        setStatus(null);
      } else {
        setStatus(data);
      }

      if (balanceRes?.isSuccess !== false) {
        const bal = balanceRes?.data ?? balanceRes?.Data ?? balanceRes;
        setPanelBalance(Number(pick(bal, "availableBalance", "AvailableBalance") ?? 0));
        setAllowPayFromBalance(
          pick(bal, "allowPayInvoiceFromBalance", "AllowPayInvoiceFromBalance") !== false
        );
      }
    } catch {
      setError("بارگذاری وضعیت پرداخت ناموفق بود.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const uiState = useMemo(() => {
    if (!status) return "loading";
    const invStatus = pick(status, "invoiceStatus", "InvoiceStatus");
    const latestSession = status.latestSession ?? status.LatestSession;
    const latestTx = status.latestTransaction ?? status.LatestTransaction;
    const sessionStatus = pick(latestSession, "status", "Status");
    const txStatus = pick(latestTx, "status", "Status");

    if (
      invStatus === 3 ||
      invStatus === "Paid" ||
      txStatus === "Succeeded" ||
      sessionStatus === GATEWAY_SESSION.Succeeded
    ) {
      return "succeeded";
    }

    if (
      sessionStatus === GATEWAY_SESSION.Failed ||
      txStatus === "Failed" ||
      invStatus === 4 ||
      invStatus === "Cancelled"
    ) {
      return "failed";
    }

    if (
      status.canResumePayment ||
      status.CanResumePayment ||
      sessionStatus === GATEWAY_SESSION.Redirected
    ) {
      return "redirected";
    }

    if (
      sessionStatus === GATEWAY_SESSION.Unknown ||
      txStatus === "Pending" ||
      sessionStatus === GATEWAY_SESSION.CallbackReceived ||
      sessionStatus === GATEWAY_SESSION.Verified ||
      sessionStatus === GATEWAY_SESSION.Settled
    ) {
      return "pending";
    }

    if (status.canPayOnline || status.CanPayOnline) {
      return "unpaid";
    }

    return "blocked";
  }, [status]);

  const badgeState = useMemo(() => {
    const latestSession = status?.latestSession ?? status?.LatestSession;
    const sessionStatus = pick(latestSession, "status", "Status");
    if (sessionStatus) return mapSessionToBadgeState(sessionStatus);
    return mapUiStateToBadgeState(uiState);
  }, [status, uiState]);

  const handleStartPayment = async () => {
    setPaying(true);
    try {
      const res = await PaymentGatewayApi.startInvoicePayment(
        invoiceId,
        getPaymentReturnUrl()
      );
      const { error: err, data } = normalizeStartPayment(res);
      if (err) {
        toast.error(err);
        return;
      }
      const redirectUrl = pick(data, "redirectUrl", "RedirectUrl");
      if (!redirectUrl) {
        toast.error("آدرس درگاه بانک دریافت نشد.");
        return;
      }
      window.location.href = normalizePaymentRedirectUrl(redirectUrl);
    } catch {
      toast.error("خطا در اتصال به درگاه پرداخت");
    } finally {
      setPaying(false);
    }
  };

  const handleContinuePayment = () => {
    const url = pick(status, "resumeRedirectUrl", "ResumeRedirectUrl");
    if (url) {
      window.location.href = normalizePaymentRedirectUrl(url);
    } else {
      toast.error("لینک ادامه پرداخت در دسترس نیست.");
    }
  };

  const remainingAmount = Number(
    pick(status, "remainingAmount", "RemainingAmount") ?? 0
  );

  const canPayFromBalance = useMemo(
    () =>
      allowPayFromBalance &&
      panelBalance != null &&
      remainingAmount > 0 &&
      panelBalance >= remainingAmount,
    [allowPayFromBalance, panelBalance, remainingAmount]
  );

  const handlePayFromBalance = async () => {
    setPayingFromBalance(true);
    try {
      const res = await CustomerBalanceApi.payInvoiceFromBalance(invoiceId);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "پرداخت از اعتبار پنل ناموفق بود.");
        return;
      }
      toast.success(res?.message || "فاکتور با اعتبار پنل پرداخت شد.");
      notifyCustomerFinanceRefresh();
      await loadStatus();
    } catch {
      toast.error("پرداخت از اعتبار پنل ناموفق بود.");
    } finally {
      setPayingFromBalance(false);
    }
  };

  if (!invoiceId) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Alert severity="info">شناسه فاکتور برای پرداخت مشخص نیست.</Alert>
      </Paper>
    );
  }

  if (loading && !status) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <PaymentCardSkeleton compact={compact} />
      </Paper>
    );
  }

  const latestSession = status?.latestSession ?? status?.LatestSession;
  const latestTx = status?.latestTransaction ?? status?.LatestTransaction;
  const provider = pick(latestSession, "provider", "Provider") ?? "Mellat";
  const isUnknownPending =
    uiState === "pending" &&
    pick(latestSession, "status", "Status") === GATEWAY_SESSION.Unknown;

  return (
    <Paper
      variant="outlined"
      data-testid="invoice-card"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        maxWidth: compact ? "100%" : 520,
        mx: compact ? 0 : { md: "auto" },
        ...(compact ? {} : { mt: 1 }),
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <CreditCardIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              پرداخت فاکتور
            </Typography>
            {status ? (
              <PaymentStatusBadge
                data-testid="paid-status"
                state={badgeState}
                label={
                  pick(status, "operationalStateTitle", "OperationalStateTitle") ||
                  undefined
                }
              />
            ) : null}
          </Stack>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={loadStatus}
            disabled={loading}
          >
            بروزرسانی
          </Button>
        </Stack>

        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadStatus}>
                تلاش مجدد
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {!error && !status ? (
          <Alert severity="info">
            اطلاعات پرداخت این فاکتور در دسترس نیست. لطفاً دوباره تلاش کنید.
          </Alert>
        ) : null}

        {!error && status ? (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              flexWrap="wrap"
              sx={{ gap: 1 }}
            >
              <Chip
                size="small"
                label={`مانده: ${formatRial(pick(status, "remainingAmount", "RemainingAmount"))}`}
                color="primary"
                variant="outlined"
              />
              {pick(status, "invoiceNumber", "InvoiceNumber") ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`شماره: ${pick(status, "invoiceNumber", "InvoiceNumber")}`}
                />
              ) : null}
            </Stack>

            {pick(status, "title", "Title") ? (
              <Typography variant="body2" color="text.secondary">
                {pick(status, "title", "Title")}
              </Typography>
            ) : null}

            <Divider />

            {uiState === "unpaid" ? (
              <>
                <Typography variant="body2">
                  مبلغ قابل پرداخت:{" "}
                  <strong>{formatRial(remainingAmount)}</strong>
                </Typography>

                {panelBalance != null && allowPayFromBalance ? (
                  <Typography variant="caption" color="text.secondary">
                    مانده اعتبار پنل: {formatRial(panelBalance)}
                  </Typography>
                ) : null}

                {canPayFromBalance ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    size="large"
                    disabled={payingFromBalance}
                    onClick={handlePayFromBalance}
                    startIcon={
                      payingFromBalance ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    پرداخت از اعتبار پنل
                  </Button>
                ) : allowPayFromBalance && panelBalance != null && remainingAmount > 0 ? (
                  <Alert severity="warning">
                    اعتبار کافی نیست.{" "}
                    <Button
                      size="small"
                      onClick={() => navigate("/SLN/Customer/Balance/TopUp")}
                    >
                      افزایش اعتبار
                    </Button>
                  </Alert>
                ) : null}

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={paying}
                  onClick={handleStartPayment}
                  data-testid="payment-button"
                  startIcon={
                    paying ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />
                  }
                >
                  پرداخت آنلاین مستقیم
                </Button>
              </>
            ) : null}

            {uiState === "redirected" ? (
              <>
                <Alert severity="info">
                  پرداخت شما آغاز شده و هنوز در درگاه بانک تکمیل نشده است.
                </Alert>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  size="large"
                  onClick={handleContinuePayment}
                >
                  ادامه پرداخت
                </Button>
              </>
            ) : null}

            {uiState === "succeeded" ? (
              <Box>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  پرداخت با موفقیت انجام شد.
                </Alert>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "action.hover" }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    رسید پرداخت
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      مبلغ:{" "}
                      {formatRial(
                        pick(latestSession, "amount", "Amount") ??
                          pick(status, "paidAmount", "PaidAmount")
                      )}
                    </Typography>
                    <Typography variant="body2">
                      درگاه: {getProviderLabel(provider)}
                    </Typography>
                    {pick(latestTx, "providerTrackingCode", "ProviderTrackingCode") ? (
                      <Typography variant="body2">
                        کد پیگیری:{" "}
                        <strong>
                          {pick(latestTx, "providerTrackingCode", "ProviderTrackingCode")}
                        </strong>
                      </Typography>
                    ) : null}
                    {pick(latestTx, "paidAt", "PaidAt") ? (
                      <Typography variant="body2">
                        تاریخ: {toJalaliFullDate(pick(latestTx, "paidAt", "PaidAt"))}
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              </Box>
            ) : null}

            {uiState === "failed" ? (
              <>
                <Alert severity="error">
                  {pick(status, "notPayableReason", "NotPayableReason") ||
                    "پرداخت ناموفق بود. در صورت امکان مجدداً تلاش کنید."}
                </Alert>
                {status.canPayOnline || status.CanPayOnline ? (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    startIcon={
                      paying ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />
                    }
                    disabled={paying}
                    onClick={handleStartPayment}
                  >
                    تلاش مجدد پرداخت
                  </Button>
                ) : null}
              </>
            ) : null}

            {uiState === "pending" ? (
              <>
                <Alert severity="info" icon={<HelpOutlineIcon />}>
                  {isUnknownPending ? (
                    <>
                      پرداخت شما ممکن است هنوز در حال نهایی‌شدن باشد. چند دقیقه صبر کنید و
                      «بروزرسانی وضعیت» را بزنید. اگر وضعیت تغییر نکرد، با پشتیبانی سولارنت
                      تماس بگیرید.
                    </>
                  ) : (
                    <>
                      پرداخت در حال پردازش است. لطفاً چند لحظه دیگر وضعیت را بروزرسانی کنید.
                    </>
                  )}
                </Alert>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={loadStatus}
                  startIcon={<RefreshIcon />}
                >
                  بروزرسانی وضعیت
                </Button>
              </>
            ) : null}

            {uiState === "blocked" ? (
              <Alert severity="warning">
                {pick(status, "notPayableReason", "NotPayableReason") ||
                  "در حال حاضر امکان پرداخت آنلاین این فاکتور وجود ندارد."}
              </Alert>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Paper>
  );
};

CustomerInvoiceOnlinePayment.propTypes = {
  invoiceId: PropTypes.number.isRequired,
  compact: PropTypes.bool,
};

export default CustomerInvoiceOnlinePayment;
